import React, { useState, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import ProductModal from './product_modal';

const pergolaTypes = [
  {
    title: 'Classic Lite Pod',
    description: 'Best value louvred-roof pergola with clean lines and a simple spec, ideal for cost-effective shelter.',
    imageUrl: '/assets/images/landing/classic_light_pod.jpg',
    features: [
      'Available in 3 set size combinations (Small / Medium / Large)',
      'LED lighting (optional)',
      'Infra-red heating (optional)',
    ],
  },
  {
    title: 'Classic Pod',
    description: 'Our most popular pergola — a refined louvred roof with integrated drainage and neat detailing.',
    imageUrl: '/assets/images/landing/classic_pod_new.jpeg',
    features: [
      'Dimensions: Length 2.38–5.95m, Width 1.50–4.50m, Height up to 3.0m',
      'Hardware colour options: Anthracite Grey (RAL 7016) + additional RAL colours',
      'Remote control and App control options',
      'Optional water & fade-resistant side screens',
      'Optional LED lighting & Infra-red heating',
      'Concealed base plates within posts',
      'Integrated rainwater pipes in posts for roof drainage',
    ],
  },
  {
    title: 'Deluxe Pod',
    description: 'Highly versatile pergola for freestanding or lean-to setups, with extensive sizing and finish options.',
    imageUrl: '/assets/images/landing/deluxe_pod_new.jpeg',
    features: [
      'Dimensions: Length 2.50–7.00m, Width 1.50–4.50m, Height up to 3.0m',
      'Hardware available in any RAL colour',
      'Optional wood-effect louvred roof finish',
      'Remote control and App control options',
      'Water & fade-resistant side screens or louvred sliding panels (optional)',
      'Posts can be inset from corners; additional posts to create doorways or roof overhangs',
      'Optional LED lighting & Infra-red heating',
    ],
  },
  {
    title: 'Deluxe + Pod',
    description: 'An elevated Deluxe with architectural detailing and larger spans for premium outdoor spaces.',
    imageUrl: '/assets/images/landing/deluxe_plus_pod.jpeg',
    features: [
      'Dimensions: Length 2.50–6.00m, Width 3.00–6.00m, Height up to 3.0m',
      'Hardware available in any RAL colour',
      'Contemporary wood-effect louvred roof option',
      'Fixed 1.5m rear glass section for natural light',
      '210mm double-walled aluminium louvres rotate 150°',
      'Streamlined framework with structured clean lines',
      'Remote control or App control options',
      'Water & fade-resistant side screens or louvred sliding panels (optional)',
      'LED lighting & Infra-red heating (optional)',
    ],
  },
  {
    title: 'Prestige Pod',
    description: 'UK-made retractable louvred-roof pergola — open the roof for maximum sun, close for full weather protection.',
    imageUrl: '/assets/images/landing/prestige.jpeg',
    features: [
      'Dimensions: Length 2.59–6.00m, Width 1.81–4.00m, Height up to 3.0m',
      'Hardware available in any RAL colour',
      'Fully retractable roof stacks to ~13% of total roof area',
      'Complete protection from rain, wind and snow when closed',
      'Roof louvres rotate 90° for sun control',
      'Remote control and App control options',
      'Water & fade-resistant side screens or louvred sliding panels (optional)',
      'LED lighting & Infra-red heating available',
    ],
  },
];


function ProductOverview() {
    const targetRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: targetRef });
    const [selectedPergola, setSelectedPergola] = useState(null);

    const x = useTransform(scrollYProgress, [0, 1], ['0%', `-${(pergolaTypes.length - 1) * 100}%`]);
    const speedMultiplier = 1.25;



    return (
        <>
            <Box ref={targetRef} sx={{ height: `${pergolaTypes.length * 100 * speedMultiplier}vh`, position: 'relative' }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        height: '100vh',
                        overflow: 'hidden',
                    }}
                >
                    <motion.div style={{ x, display: 'flex', height: '100%' }}>
                        {pergolaTypes.map((pergola, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    width: '100vw',
                                    height: '100vh',
                                    flexShrink: 0,
                                    backgroundImage: `url(${pergola.imageUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.4)' }} />
                                
                                {/* --- ✅ CORRECTED SECTION --- */}
                                <Box sx={{ textAlign: 'center', zIndex: 1, p: 4 }}>
                                    <Typography
                                        variant="h2"
                                        component="h3"
                                        fontWeight="bold"
                                        letterSpacing={5}
                                        color='rgb(22, 161, 216)'
                                        sx={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.3)' }}
                                    >
                                        {pergola.title}
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        color="white" // Explicitly set color here
                                        sx={{
                                            mt: 2,
                                            maxWidth: '600px',
                                            textShadow: '1px 1px 4px rgba(0, 0, 0, 0.5)',
                                        }}
                                    >
                                        {pergola.description}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ mt: 2 }}
                                        onClick={() => setSelectedPergola(pergola)}
                                    >
                                        LEARN MORE
                                    </Button>
                                </Box>

                            </Box>
                        ))}
                    </motion.div>
                </Box>
            </Box>

            <AnimatePresence>
                {selectedPergola && (
                    <ProductModal
                        pergola={selectedPergola}
                        onClose={() => setSelectedPergola(null)}
                    />
                )}
            </AnimatePresence>
        </>
    );
}

export default ProductOverview;